/**
 * SequencerPanel — UI for the step Sequencer module.
 *
 * Layout:
 *
 *   ┌─ Transport row ───────────────────────────────────────────────┐
 *   │ [▶ Off/On]  Rate ▼ Length [16]  Swing 0%                       │
 *   ├─ Step grid (length × cells) ──────────────────────────────────┤
 *   │ ┌─┐ ┌─┐ ┌─┐ ┌─┐ ┌─┐ ┌─┐ ┌─┐ ┌─┐ …                              │
 *   │ │C3│ │·│ │E3│ │·│ │G3│ │·│ │D3│ │A3│                            │
 *   │ │▮│ │ │ │▮│ │ │ │▮│ │ │ │▮│ │▮│   ← velocity bar at bottom     │
 *   │ └─┘ └─┘ └─┘ └─┘ └─┘ └─┘ └─┘ └─┘                                  │
 *   ├─ Mod lane ─────────────────────────────────────────────────────┤
 *   │ ┌─┐ ┌─┐ ┌─┐ ┌─┐ ┌─┐ ┌─┐ ┌─┐ ┌─┐ …                              │
 *   │ │▓│ │░│ │▓│ │░│ │▓│ │▓│ │░│ │▓│   ← bar height = modValue       │
 *   │ └─┘ └─┘ └─┘ └─┘ └─┘ └─┘ └─┘ └─┘                                  │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Interactions:
 *   - Click a step cell: toggle active.
 *   - Vertical drag inside a step cell: change pitch (1 px = 1 semitone).
 *   - Right-click a step cell: open a small editor (velocity + length).
 *   - Vertical drag on a mod cell: change modValue.
 *
 * The cursor highlight tracks `seq.getCurrentStepIndex()` and refreshes
 * via the Sequencer's subscribe channel — no rAF loop, since the
 * sequencer fires its own listener on every tick.
 */

import type { Sequencer } from '@ac-slayer/synth-core';
import { SEQ_MAX_STEPS } from '@ac-slayer/synth-core';
import { generateMusicalSequence, SCALE_NAMES, type ScaleName } from '@ac-slayer/synth-core';
import type { PatternStore } from '@ac-slayer/synth-core';
import { PatternBrowser } from './PatternBrowser';
import { midiToName } from '@ac-slayer/synth-core';
import { NumericParameter } from '@ac-slayer/synth-core';
import { Knob } from '../components/Knob';

/** Pixels of vertical drag that span one full pitch range. Matches the
 *  Knob.dragSpanPx feel — long enough to land on a specific note,
 *  short enough that wide pitch sweeps don't need a forearm workout. */
const PITCH_DRAG_SPAN_PX = 200;
/** Pixels of vertical drag that span the full mod value range. */
const MOD_DRAG_SPAN_PX = 100;

interface CellRefs {
  readonly cell: HTMLElement;
  readonly noteEl: HTMLElement;
  readonly velocityEl: HTMLElement;
  readonly tieEl: HTMLElement;
  readonly modCell: HTMLElement;
  readonly modBar: HTMLElement;
}

export interface SequencerPanelOptions {
  readonly sequencer: Sequencer;
  /**
   * Optional pattern store. When supplied, the panel exposes a
   * Patterns dropdown + Save / Delete controls that round-trip
   * sequencer state through localStorage. Omitting the store hides
   * the controls — useful for tests + standalone embedding.
   */
  readonly patternStore?: PatternStore;
  /**
   * Called right before the play button toggles the sequencer ON.
   * main.ts wires this to `ensureAudio()` so the AudioContext
   * unlocks when the user hits play — without this hook the
   * sequencer ticks fire while audio is still suspended and no
   * sound comes out until some other interaction (e.g. playing
   * the keyboard) unlocks it.
   */
  readonly onPlay?: () => void | Promise<void>;
}

export class SequencerPanel {
  readonly element: HTMLElement;
  private readonly seq: Sequencer;
  private readonly patternStore: PatternStore | null;
  private readonly cells: CellRefs[] = [];
  private readonly stepGrid: HTMLElement;
  private readonly modGrid: HTMLElement;
  private readonly enableBtn: HTMLButtonElement;
  private readonly rateSelect: HTMLSelectElement;
  private readonly lengthInput: HTMLInputElement;
  private readonly swingInput: HTMLInputElement;
  private readonly scaleSelect: HTMLSelectElement;
  private readonly patternBrowser: PatternBrowser | null;
  private readonly unsubscribe: () => void;
  private editorEl: HTMLElement | null = null;
  /** Knobs alive inside the currently-open editor, plus their
   *  unsubscribe handles. Cleared on closeEditor so a re-open builds
   *  fresh ones bound to the latest step values. */
  private editorKnobs: Knob[] = [];
  private editorUnsubs: Array<() => void> = [];
  /** Currently-loaded pattern id, or null when the user has edited
   *  the pattern after load (so the next Save makes a new entry
   *  rather than overwriting). */
  private loadedPatternId: string | null = null;

  private readonly onPlay: (() => void | Promise<void>) | null;

  constructor(opts: SequencerPanelOptions) {
    this.seq = opts.sequencer;
    this.patternStore = opts.patternStore ?? null;
    this.onPlay = opts.onPlay ?? null;

    this.element = document.createElement('div');
    this.element.className = 'seq-panel';
    this.element.setAttribute('role', 'group');
    this.element.setAttribute('aria-label', 'Sequencer');

    // ── Transport row ────────────────────────────────────────────
    const transport = document.createElement('div');
    transport.className = 'seq-transport';

    this.enableBtn = document.createElement('button');
    this.enableBtn.type = 'button';
    this.enableBtn.className = 'btn btn-sm seq-enable';
    this.enableBtn.setAttribute('aria-pressed', 'false');
    this.enableBtn.addEventListener('click', () => {
      const turningOn = this.seq.enabled.value !== 'on';
      // Run the play hook (typically ensureAudio()) so the
      // AudioContext is unlocked before the first tick fires —
      // without this, hitting play before any other audio-trigger
      // gesture leaves the sequencer ticking silently. Hook is
      // fire-and-forget; toggling enabled doesn't need to wait
      // since the first scheduled tick has its own ~5ms lead-in.
      if (turningOn && this.onPlay) {
        try {
          void this.onPlay();
        } catch {
          /* hook errors shouldn't block playback toggle */
        }
      }
      this.seq.enabled.set(turningOn ? 'on' : 'off', 'ui');
    });
    transport.appendChild(this.enableBtn);

    const rateWrap = makeLabeled('Rate');
    this.rateSelect = document.createElement('select');
    this.rateSelect.className = 'seq-select mono';
    for (const r of ['1/2', '1/4', '1/4T', '1/8', '1/8T', '1/16', '1/16T'] as const) {
      const opt = document.createElement('option');
      opt.value = r;
      opt.textContent = r;
      this.rateSelect.appendChild(opt);
    }
    this.rateSelect.value = this.seq.rate.value;
    this.rateSelect.addEventListener('change', () => {
      this.seq.rate.set(this.rateSelect.value as never, 'ui');
    });
    rateWrap.appendChild(this.rateSelect);
    transport.appendChild(rateWrap);

    const lenWrap = makeLabeled('Length');
    this.lengthInput = document.createElement('input');
    this.lengthInput.type = 'number';
    this.lengthInput.className = 'seq-number mono';
    this.lengthInput.min = '1';
    this.lengthInput.max = String(SEQ_MAX_STEPS);
    this.lengthInput.step = '1';
    this.lengthInput.value = String(Math.round(this.seq.length.value));
    this.lengthInput.addEventListener('change', () => {
      const v = parseInt(this.lengthInput.value, 10);
      if (Number.isFinite(v)) this.seq.length.set(v, 'ui');
    });
    lenWrap.appendChild(this.lengthInput);
    transport.appendChild(lenWrap);

    const swingWrap = makeLabeled('Swing');
    this.swingInput = document.createElement('input');
    this.swingInput.type = 'range';
    this.swingInput.className = 'range-themed seq-swing';
    this.swingInput.min = '0';
    this.swingInput.max = '0.5';
    this.swingInput.step = '0.01';
    this.swingInput.value = String(this.seq.swing.value);
    this.swingInput.addEventListener('input', () => {
      this.seq.swing.set(parseFloat(this.swingInput.value), 'ui');
    });
    swingWrap.appendChild(this.swingInput);
    transport.appendChild(swingWrap);

    // Scale picker — feeds the musical randomizer. Defaults to minor
    // pentatonic since just about anything sampled from it sounds at
    // least passable. Stored as a plain field on the panel; the
    // engine itself is scale-agnostic.
    const scaleWrap = makeLabeled('Scale');
    this.scaleSelect = document.createElement('select');
    this.scaleSelect.className = 'seq-select mono';
    for (const s of SCALE_NAMES) {
      const opt = document.createElement('option');
      opt.value = s;
      opt.textContent = s;
      this.scaleSelect.appendChild(opt);
    }
    this.scaleSelect.value = 'minor-pentatonic';
    scaleWrap.appendChild(this.scaleSelect);
    transport.appendChild(scaleWrap);

    // Randomize button — generates a fresh musical pattern in the
    // current scale + applies it via setSteps in one shot. Editing
    // any step afterward "forks" the loaded pattern (loadedPatternId
    // clears) so the next Save creates a new entry.
    const randomBtn = document.createElement('button');
    randomBtn.type = 'button';
    randomBtn.className = 'btn btn-sm seq-random';
    randomBtn.textContent = '⚂ Random';
    randomBtn.title =
      'Generate a fresh musical pattern in the selected scale';
    randomBtn.addEventListener('click', () => this.randomizePattern());
    transport.appendChild(randomBtn);

    // Patterns button — opens a full CRUD modal (PatternBrowser) for
    // saving / loading / renaming / deleting sequencer patterns.
    // Hidden when no PatternStore was supplied so tests + standalone
    // embedding can skip the persistence layer entirely.
    if (this.patternStore) {
      const patternsBtn = document.createElement('button');
      patternsBtn.type = 'button';
      patternsBtn.className = 'btn btn-sm seq-patterns-btn';
      patternsBtn.textContent = '☰ Patterns';
      patternsBtn.title = 'Browse, save, and manage sequencer patterns';
      this.patternBrowser = new PatternBrowser({
        store: this.patternStore,
        sequencer: this.seq,
        onLoaded: (id) => {
          this.loadedPatternId = id;
        },
      });
      // The modal mounts at body so it overlays everything else; the
      // panel just owns the open trigger.
      document.body.appendChild(this.patternBrowser.element);
      patternsBtn.addEventListener('click', () => {
        this.patternBrowser!.setActiveId(this.loadedPatternId);
        this.patternBrowser!.open();
      });
      transport.appendChild(patternsBtn);
    } else {
      this.patternBrowser = null;
    }

    this.element.appendChild(transport);

    // ── Step grid ────────────────────────────────────────────────
    // Each grid sits inside a labeled row so the function of the
    // line is obvious at a glance — NOTES on top (pitch + velocity),
    // MOD below (the per-step value driving the STEP mod source).
    // Both rows flex equally so the cells across both rows render
    // at the same height regardless of how tall the panel is.
    const stepRow = document.createElement('div');
    stepRow.className = 'seq-row seq-row-notes';
    const stepLabel = document.createElement('span');
    stepLabel.className = 'seq-row-label label';
    stepLabel.textContent = 'NOTES';
    this.stepGrid = document.createElement('div');
    this.stepGrid.className = 'seq-grid seq-grid-steps';
    stepRow.append(stepLabel, this.stepGrid);
    this.element.appendChild(stepRow);

    // ── Mod lane ─────────────────────────────────────────────────
    const modRow = document.createElement('div');
    modRow.className = 'seq-row seq-row-mod';
    const modLabel = document.createElement('span');
    modLabel.className = 'seq-row-label label';
    modLabel.textContent = 'MOD';
    this.modGrid = document.createElement('div');
    this.modGrid.className = 'seq-grid seq-grid-mod';
    modRow.append(modLabel, this.modGrid);
    this.element.appendChild(modRow);

    // Build all SEQ_MAX_STEPS cells; visibility is gated by length below.
    for (let i = 0; i < SEQ_MAX_STEPS; i++) {
      this.cells.push(this.buildCell(i));
    }

    // Subscriptions — re-render on any sequencer change. Cheap because
    // we only touch the cells that need updating.
    this.unsubscribe = this.seq.subscribe(() => this.refresh());

    // Mirror the sequencer's parameters into the controls when they
    // change from elsewhere (preset load, randomize, etc.).
    this.seq.enabled.subscribe(() => this.refreshTransport());
    this.seq.rate.subscribe(() => this.refreshTransport());
    this.seq.length.subscribe(() => this.refresh());
    this.seq.swing.subscribe(() => this.refreshTransport());

    // Close any open editor when the user clicks outside it.
    document.addEventListener('mousedown', this.onDocumentMouseDown);

    this.refresh();
    this.refreshTransport();
  }

  dispose(): void {
    this.unsubscribe();
    this.patternBrowser?.dispose();
    if (this.patternBrowser?.element.parentElement) {
      this.patternBrowser.element.parentElement.removeChild(
        this.patternBrowser.element,
      );
    }
    this.closeEditor();
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
  }

  // ── Randomize control ───────────────────────────────────────────────

  private randomizePattern(): void {
    const scale = (this.scaleSelect.value as ScaleName) || 'minor-pentatonic';
    const steps = generateMusicalSequence({
      scale,
      // Match the panel's playback length so generation respects the
      // user's chosen bar length. The full step buffer is filled
      // anyway (clipped at SEQ_MAX_STEPS); only the first `length`
      // are audible.
      stepCount: SEQ_MAX_STEPS,
    });
    this.seq.setSteps(steps);
    // A randomized pattern is brand-new, not a "load" — clear the
    // currently-loaded id so the next Save makes a fresh entry.
    this.loadedPatternId = null;
    this.patternBrowser?.setActiveId(null);
  }

  // ── Internals ─────────────────────────────────────────────────────────

  private buildCell(index: number): CellRefs {
    const cell = document.createElement('button');
    cell.type = 'button';
    cell.className = 'seq-cell';
    cell.dataset.index = String(index);
    cell.setAttribute('aria-label', `Step ${index + 1}`);
    cell.addEventListener('click', () => {
      const cur = this.seq.steps[index]!;
      this.seq.setStep(index, { active: !cur.active });
    });
    cell.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this.openEditor(index, cell);
    });
    this.attachPitchDrag(cell, index);

    const noteEl = document.createElement('span');
    noteEl.className = 'seq-cell-note mono';
    cell.appendChild(noteEl);

    const velocityEl = document.createElement('span');
    velocityEl.className = 'seq-cell-velocity';
    cell.appendChild(velocityEl);

    // Tie indicator — small label showing the tie span (e.g. "×3")
    // when length > 1. Hidden otherwise.
    const tieEl = document.createElement('span');
    tieEl.className = 'seq-cell-tie';
    cell.appendChild(tieEl);

    this.stepGrid.appendChild(cell);

    // Mod cell ─ paired by index. Click+drag to set value.
    const modCell = document.createElement('button');
    modCell.type = 'button';
    modCell.className = 'seq-mod-cell';
    modCell.dataset.index = String(index);
    modCell.setAttribute('aria-label', `Step ${index + 1} mod value`);
    const modBar = document.createElement('span');
    modBar.className = 'seq-mod-bar';
    modCell.appendChild(modBar);
    this.attachModDrag(modCell, index);
    this.modGrid.appendChild(modCell);

    return { cell, noteEl, velocityEl, tieEl, modCell, modBar };
  }

  private attachPitchDrag(cell: HTMLElement, index: number): void {
    let pointerId: number | null = null;
    let startY = 0;
    let startNote = 0;
    let didDrag = false;

    cell.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      pointerId = e.pointerId;
      startY = e.clientY;
      startNote = this.seq.steps[index]?.note ?? 60;
      didDrag = false;
      cell.setPointerCapture(e.pointerId);
    });
    cell.addEventListener('pointermove', (e) => {
      if (pointerId !== e.pointerId) return;
      const dy = startY - e.clientY; // up = positive
      // Map drag span to a 4-octave pitch range so a single drag can
      // move a note across most of a synth's playable range.
      const semitones = Math.round((dy / PITCH_DRAG_SPAN_PX) * 48);
      if (semitones !== 0) didDrag = true;
      const next = Math.max(0, Math.min(127, startNote + semitones));
      this.seq.setStep(index, { note: next });
    });
    const release = (e: PointerEvent): void => {
      if (pointerId !== e.pointerId) return;
      try {
        cell.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      pointerId = null;
      // If the user dragged, suppress the click handler that would
      // otherwise toggle active — they meant to set pitch, not flip
      // the gate. We do this by canceling the next click event.
      if (didDrag) {
        const cancel = (clickEv: MouseEvent): void => {
          clickEv.stopPropagation();
          clickEv.preventDefault();
          cell.removeEventListener('click', cancel, true);
        };
        cell.addEventListener('click', cancel, true);
      }
    };
    cell.addEventListener('pointerup', release);
    cell.addEventListener('pointercancel', release);
  }

  private attachModDrag(cell: HTMLElement, index: number): void {
    let pointerId: number | null = null;
    let startY = 0;
    let startValue = 0;
    cell.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      pointerId = e.pointerId;
      startY = e.clientY;
      startValue = this.seq.steps[index]?.modValue ?? 0.5;
      cell.setPointerCapture(e.pointerId);
      e.preventDefault();
    });
    cell.addEventListener('pointermove', (e) => {
      if (pointerId !== e.pointerId) return;
      const dy = startY - e.clientY;
      const next = Math.max(0, Math.min(1, startValue + dy / MOD_DRAG_SPAN_PX));
      this.seq.setStep(index, { modValue: next });
    });
    const release = (e: PointerEvent): void => {
      if (pointerId !== e.pointerId) return;
      try {
        cell.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      pointerId = null;
    };
    cell.addEventListener('pointerup', release);
    cell.addEventListener('pointercancel', release);
  }

  private openEditor(index: number, anchor: HTMLElement): void {
    this.closeEditor();
    const step = this.seq.steps[index];
    if (!step) return;

    const editor = document.createElement('div');
    editor.className = 'seq-editor';
    editor.setAttribute('role', 'dialog');
    const title = document.createElement('div');
    title.className = 'seq-editor-title label';
    title.textContent = `Step ${index + 1}`;
    editor.appendChild(title);

    // Each field gets a real Knob, same as the synth rack. Knobs
    // need a NumericParameter to bind to, so we mint short-lived
    // wrappers around the step's current values; their subscribe
    // callbacks write back through `setStep`. The wrappers live
    // inside `editorKnobs` / `editorUnsubs` and get disposed when
    // the editor closes — they don't leak between openings.
    const knobsWrap = document.createElement('div');
    knobsWrap.className = 'seq-editor-knobs';
    editor.appendChild(knobsWrap);

    knobsWrap.appendChild(
      this.makeEditorKnob('Note', step.note, {
        min: 0,
        max: 127,
        // Display the MIDI number as a note name (C3, F#5, …) so the
        // user reads "what pitch is this" instead of doing the
        // semitone arithmetic in their head.
        formatter: (v) => midiToName(Math.round(v)),
        smoothingMs: 0,
        onChange: (v) => this.seq.setStep(index, { note: v }),
      }),
    );
    knobsWrap.appendChild(
      this.makeEditorKnob('Vel', step.velocity, {
        min: 0,
        max: 1,
        formatter: (v) => `${Math.round(v * 100)}%`,
        smoothingMs: 0,
        onChange: (v) => this.seq.setStep(index, { velocity: v }),
      }),
    );
    knobsWrap.appendChild(
      this.makeEditorKnob('Length', step.length, {
        min: 1,
        max: SEQ_MAX_STEPS,
        formatter: (v) => `${Math.round(v)}`,
        smoothingMs: 0,
        onChange: (v) => this.seq.setStep(index, { length: v }),
      }),
    );
    knobsWrap.appendChild(
      this.makeEditorKnob('Mod', step.modValue, {
        min: 0,
        max: 1,
        formatter: (v) => `${Math.round(v * 100)}%`,
        smoothingMs: 0,
        onChange: (v) => this.seq.setStep(index, { modValue: v }),
      }),
    );

    // Mount on body and use viewport-fixed positioning so the editor
    // can never be clipped by the seq-panel / keyboard-dock overflow.
    // Flip horizontally + vertically when the natural placement would
    // run off-screen so all four fields are always reachable.
    editor.style.position = 'fixed';
    editor.style.visibility = 'hidden'; // measure first, then place
    document.body.appendChild(editor);
    this.editorEl = editor;

    const anchorRect = anchor.getBoundingClientRect();
    const editorRect = editor.getBoundingClientRect();
    const margin = 8;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // Default: pinned to the cell's right edge, top-aligned.
    let left = anchorRect.right + 4;
    let top = anchorRect.top;
    // Flip to the cell's left if it would overflow the right edge.
    if (left + editorRect.width + margin > viewportW) {
      left = anchorRect.left - editorRect.width - 4;
    }
    // Final clamp — if both flips fail (very narrow viewport),
    // pin to whichever edge keeps the editor on-screen.
    left = Math.max(margin, Math.min(left, viewportW - editorRect.width - margin));
    if (top + editorRect.height + margin > viewportH) {
      top = viewportH - editorRect.height - margin;
    }
    top = Math.max(margin, top);
    editor.style.left = `${left}px`;
    editor.style.top = `${top}px`;
    editor.style.visibility = '';
  }

  private makeEditorKnob(
    label: string,
    initialValue: number,
    opts: {
      min: number;
      max: number;
      formatter?: (v: number) => string;
      smoothingMs?: number;
      onChange: (v: number) => void;
    },
  ): HTMLElement {
    // Mint a short-lived NumericParameter wrapping the step's value.
    // The Knob component binds to it the same way every other knob in
    // the synth does. We forward 'ui'-source changes back to the
    // sequencer via setStep — which fires the panel's subscribe and
    // re-renders the cells, but `applyInProgress` isn't needed here
    // because the editor's knob is the only writer.
    const param = new NumericParameter({
      name: label,
      default: initialValue,
      min: opts.min,
      max: opts.max,
      curve: 'linear',
      smoothingMs: opts.smoothingMs ?? 0,
      ...(opts.formatter ? { formatter: opts.formatter } : {}),
    });
    // Seed with the actual current value rather than the default so
    // re-opens land where the step is, not at min/default.
    param.set(initialValue, 'init');
    const unsub = param.subscribe((v, source) => {
      if (source === 'init' || source === 'preset') return;
      opts.onChange(v);
    });
    const knob = new Knob({ param, size: 44, dragSpanPx: 160 });
    this.editorKnobs.push(knob);
    this.editorUnsubs.push(unsub);
    return knob.element;
  }

  private closeEditor(): void {
    for (const k of this.editorKnobs) k.dispose();
    this.editorKnobs.length = 0;
    for (const u of this.editorUnsubs) u();
    this.editorUnsubs.length = 0;
    if (this.editorEl) {
      this.editorEl.remove();
      this.editorEl = null;
    }
  }

  private readonly onDocumentMouseDown = (e: MouseEvent): void => {
    if (!this.editorEl) return;
    if (e.target instanceof Node && this.editorEl.contains(e.target)) return;
    this.closeEditor();
  };

  /** Refresh every cell — paint note name, active state, velocity bar,
   *  cursor highlight, gate visibility based on length, plus tie
   *  carrier shading for cells covered by a previous step's tie. */
  private refresh(): void {
    const len = Math.round(this.seq.length.value);
    const cursor = this.seq.getCurrentStepIndex();

    // Pre-compute which cells are covered by a tie. Walk the visible
    // window forward — when an active step has length > L, the next
    // L-1 cells are carriers and shouldn't read as independently
    // playing notes. Stays inside [0, len) since ties wrap with the
    // pattern length, not the full step buffer.
    const carrier = new Array(this.cells.length).fill(false);
    for (let i = 0; i < len; i++) {
      const s = this.seq.steps[i];
      if (!s || !s.active || s.length <= 1) continue;
      const span = Math.min(s.length - 1, len - i - 1);
      for (let k = 1; k <= span; k++) carrier[i + k] = true;
    }

    for (let i = 0; i < this.cells.length; i++) {
      const refs = this.cells[i]!;
      const step = this.seq.steps[i]!;
      const visible = i < len;
      const isCarrier = visible && carrier[i];
      refs.cell.hidden = !visible;
      refs.modCell.hidden = !visible;
      refs.cell.classList.toggle('is-active', step.active && !isCarrier);
      refs.cell.classList.toggle('is-carrier', isCarrier);
      refs.cell.classList.toggle('is-cursor', i === cursor);
      refs.modCell.classList.toggle('is-cursor', i === cursor);
      refs.noteEl.textContent = isCarrier
        ? '↩'
        : step.active
          ? noteShort(step.note)
          : '·';
      refs.velocityEl.style.height = `${Math.round(step.velocity * 100)}%`;
      // Tie badge: only on the originating step, only when length > 1.
      if (step.active && step.length > 1 && !isCarrier) {
        refs.tieEl.textContent = `×${step.length}`;
        refs.tieEl.hidden = false;
      } else {
        refs.tieEl.textContent = '';
        refs.tieEl.hidden = true;
      }
      refs.modBar.style.height = `${Math.round(step.modValue * 100)}%`;
    }
  }

  private refreshTransport(): void {
    const on = this.seq.enabled.value === 'on';
    this.enableBtn.textContent = on ? '■ STOP' : '▶ PLAY';
    this.enableBtn.classList.toggle('is-on', on);
    this.enableBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    this.rateSelect.value = this.seq.rate.value;
    this.lengthInput.value = String(Math.round(this.seq.length.value));
    this.swingInput.value = String(this.seq.swing.value);
  }
}

/** Build a small label-wrapped container — used by the transport row. */
function makeLabeled(text: string): HTMLLabelElement {
  const wrap = document.createElement('label');
  wrap.className = 'seq-labeled';
  const lbl = document.createElement('span');
  lbl.className = 'label';
  lbl.textContent = text;
  wrap.appendChild(lbl);
  return wrap;
}

/** Short-form note name fitting in a step cell — drops the octave's
 *  minus sign for negative octaves to keep widths consistent. */
function noteShort(midi: number): string {
  return midiToName(midi);
}
